import React, { forwardRef, useImperativeHandle } from "react";
import { Form, Input, Select, DatePicker, Button, Checkbox, Row, Col } from "antd";
import SOURCES from "../../utils/constants";
import { cn } from "../../utils/cn";

const { TextArea } = Input;

const CommonForm = forwardRef(({
  data,
  onSubmit, // Legacy support
  onFinish,
  onValuesChange,
  form: propForm,
  layoutType = "vertical",
  isInternalSubmitt = false,
  className,
}, ref) => {
  const [internalForm] = Form.useForm();
  const form = propForm || internalForm;

  useImperativeHandle(ref, () => ({
    submit: () => form.submit(),
    setFieldsValue: (values) => form.setFieldsValue(values),
    getFieldsValue: () => form.getFieldsValue(),
    validateFields: () => form.validateFields(),
    resetFields: () => form.resetFields(),
  }));

  const handleFinish = (values) => {
    if (onFinish) onFinish(values);
    else if (onSubmit) onSubmit(values);
  };

  return (
    <Form
      layout={layoutType}
      form={form}
      onFinish={handleFinish}
      onValuesChange={onValuesChange}
      validateTrigger={["onChange", "onBlur"]}
      className={className}
    >
      <Row gutter={[4, 4]}>
        {data?.map((item, index) => {
          const commonItemProps = {
            label: item.type === "checkbox" ? "" : item.label,
            name: item.name,
            rules: item.required
              ? item.name === "confirmPassword"
                ? [
                  { required: true, message: item.message || "Confirm password is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || (getFieldValue("password") === value || getFieldValue("newPassword") === value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]
                : item.name === "password" && item.validate !== false
                  ? [
                    { required: true, message: item.message || "Password is required" },
                    {
                      pattern: item.isLogIn ? "" : /^(?=.*[A-Z])(?=.*\d).{8,}$/,
                      message: "Password must include at least one uppercase letter, one number, and be at least 8 characters long.",
                    },
                  ]
                  : [{ required: true, message: item.message || `${item.label} is required` }]
              : [],
            initialValue: item.initialValue,
            valuePropName: item.type === "checkbox" ? "checked" : "value",
            extra: item.subTitle && <p className="text-xs text-gray-500 italic mt-1">{item.subTitle}</p>,
          };

          if (item.inputType === SOURCES.FORMINPUTTYPES.INPUT) {
            return (
              <Col xs={24} sm={24} md={item.span || 12} key={item.name || index}>
                <Form.Item
                  {...commonItemProps}
                  dependencies={item.name === "confirmPassword" ? ["password"] : []}
                  validateFirst
                >
                  {item.type === "password" ? (
                    <Input.Password
                      placeholder={item.placeholder}
                      disabled={item.disabled}
                      readOnly={item.readOnly}
                      onChange={item.onChange ? item.onChange : () => { }}
                      className="h-10 w-full border hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30"
                    />
                  ) : item.type === "textarea" ? (
                    <TextArea
                      placeholder={item.placeholder}
                      rows={item.rows || 4}
                      className="w-full border hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30"
                    />
                  ) : item.type === "checkbox" ? (
                    <Checkbox
                      className={cn(
                        "flex items-center",
                        "[&_.ant-checkbox-inner]:scale-[1.25]",
                        // "[&_.ant-checkbox-inner]:h-5",
                        "[&_.ant-checkbox-inner]:rounded-sm",
                        "[&_.ant-checkbox-inner]:border-gray-300",
                        "[&_.ant-checkbox:hover_.ant-checkbox-inner]:border-primary",
                        "[&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-primary",
                        "[&_.ant-checkbox-checked:hover_.ant-checkbox-inner]:!bg-primary",
                        "[&_.ant-checkbox-checked_.ant-checkbox-inner]:border-primary",
                        // "[&_.ant-checkbox-inner:after]:!w-2",
                        // "[&_.ant-checkbox-inner:after]:!h-3"
                      )}
                    >
                      {item.label}
                    </Checkbox>
                  ) : (
                    <Input
                      placeholder={item.placeholder}
                      type={item.type}
                      disabled={item.disabled}
                      readOnly={item.readOnly}
                      onChange={item.onChange ? item.onChange : () => { }}
                      className={cn("h-10 w-full border hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30",

                      )}
                    />
                  )}
                </Form.Item>
              </Col>
            );
          }

          if (item.inputType === SOURCES.FORMINPUTTYPES.DATE) {
            return (
              <Col xs={24} sm={24} md={item.span || 12} key={item.name || index}>
                <Form.Item {...commonItemProps}>
                  <DatePicker className="h-10 w-full border hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30" />
                </Form.Item>
              </Col>
            );
          }

          if (item.inputType === SOURCES.FORMINPUTTYPES.SELECT) {
            return (
              <Col xs={24} sm={24} md={item.span || 12} key={item.name || index}>
                <Form.Item {...commonItemProps}>
                  <Select
                    options={item.options}
                    placeholder={item.placeholder}
                    loading={item.loading}
                    className={cn("h-10 w-full hover:!border-primary focus:!border-primary rounded-lg bg-gray-50/30", `
        [&_.ant-select-selector]:hover:!border-primary
        [&_.ant-select-selector]:focus-within:!border-primary
        [&_.ant-select-selector]:focus-visible:!border-primary
        `)}
                    onChange={item.onChange ? item.onChange : () => { }}
                  />
                </Form.Item>
              </Col>
            );
          }

          if (item.inputType === SOURCES.FORMINPUTTYPES.CUSTOM) {
            return (
              <Col xs={24} sm={24} md={item.span || 12} key={item.name || index} className={cn(item.className)}>
                <Form.Item {...commonItemProps}>
                  {item.component}
                </Form.Item>
              </Col>
            );
          }
          return null;
        })}
      </Row>
      {isInternalSubmitt && (
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      )}
    </Form>
  );
});

export default CommonForm;
